/**
 * Subscription API client (web). Typed wrappers for the customer subscription
 * endpoints. NOTE: these are **un-versioned** (`/api/subscription/*`, no `/v1`),
 * matching the backend + the mobile app.
 */
import { apiClient } from '../../../services/apiClient';

export interface QuotaUsage {
  quotaType: string;
  current: number;
  effectiveLimit: number;
  hasOverride: boolean;
  percentUsed: number;
  atLimit: boolean;
  nearLimit: boolean;
}

export interface SubscriptionStatusResponse {
  id: string;
  plan: string;
  intendedPlan?: string;
  status: string;
  trialEndDate?: string;
  trialExtensionCount: number;
  originalTrialEndDate?: string;
  currentPeriodStart?: string;
  currentPeriodEnd?: string;
  cancelAtPeriodEnd: boolean;
  pendingPlan?: string;
  gracePeriodEnd?: string;
  /** Derived enforcement state (FR-058): 'allowed' | 'restricted' | 'locked'. */
  accessStatus?: string;
  /** Server-computed whole days left in trial (FR-003/FR-056); preferred over client-clock math. */
  trialDaysRemaining?: number;
  features: string[];
  usage: QuotaUsage[];
}

export interface SubscriptionPlanInfo {
  id: string;
  name: string;
  description: string;
  priceMonthly: number;
  priceAnnual: number;
  currency: string;
  trialDays: number;
  displayOrder: number;
  recommended: boolean;
  defaultQuotas: Record<string, number>;
  features: string[];
}

export interface PlansResponse {
  plans: SubscriptionPlanInfo[];
}

export interface UsageResponse {
  quotas: QuotaUsage[];
}

export const getSubscriptionStatus = () =>
  apiClient.get<SubscriptionStatusResponse>('/api/subscription/status');

export const getSubscriptionPlans = () =>
  apiClient.get<PlansResponse>('/api/subscription/plans');

export const getSubscriptionUsage = () =>
  apiClient.get<UsageResponse>('/api/subscription/usage');
